import { CameraLayers, getQueryParameter, isPowerOfTwo } from "flavr-common";
import { mainScene } from "../mainScene";
import { environmentSceneService } from "../background/EnvironmentSceneService";
import { CubeCamera, IcosahedronGeometry, LinearMipmapLinearFilter, Mesh, MeshStandardMaterial, SRGBColorSpace, WebGLCubeRenderTarget } from "three";
import { FacetRenderer, RenderableUpdateTriggerState } from "../renderable/Renderable";
import { now } from "../../../messages/timing";
import { ReflectionProbeSystem } from "./ReflectionProbeSystem";
const DEBUG = !!getQueryParameter(window.location.href, "debug");
export class ReflectionProbeRenderable extends FacetRenderer {
    constructor(owner) {
        super(owner);
        this.owner = owner;
        this.needsUpdate = false;
        this.lastUpdate = 0;
        this.onUpdate = () => {
            this.needsUpdate = true;
        };
        this.update = () => {
            if (!this.needsUpdate)
                return;
            if (now() - this.lastUpdate < 1000)
                return;
            this.lastUpdate = now();
            this.generate();
            this.needsUpdate = false;
        };
    }
    setVisible(visible) {
        if (visible) {
            this.generate();
        }
        else {
            environmentSceneService.changeToDefaultEnvironment();
        }
    }
    deleteEnvMap() {
        if (this.envMap === undefined)
            return;
        this.envMap.dispose();
        this.envMap = undefined;
    }
    destroy() {
        environmentSceneService.changeToDefaultEnvironment();
        if (this.debugSphere) {
            if (this.debugSphere.parent) {
                this.debugSphere.parent.remove(this.debugSphere);
            }
            this.debugSphere = undefined;
            this.deleteEnvMap();
        }
        this.owner.renderer.getSystem(ReflectionProbeSystem).onUpdateReflectionProbeDelegate.remove(this.onUpdate);
    }
    onInit(facet) {
        this.registerAnimationCallback(this.update);
        this.facet = facet;
        this.createRenderTarget();
        this.owner.renderer.getSystem(ReflectionProbeSystem).onUpdateReflectionProbeDelegate.add(this.onUpdate);
    }
    updateFacet() {
        this.needsUpdate = true;
    }
    updateTransform() {
        if (this.cubeCamera) {
            this.cubeCamera.position.copy(this.owner.worldPosition);
        }
        this.generate();
        if (this.debugSphere) {
            this.debugSphere.matrix.copy(this.owner.matrixWorld);
            this.debugSphere.updateMatrixWorld(true);
        }
    }
    generate() {
        if (!this.facet)
            return;
        const busyBatch = this.owner.renderer.getBusyIndicatorBatch(this.owner.modelUID);
        const busyVisible = busyBatch.getVisible();
        busyBatch.setVisible(false);
        this.deleteEnvMap();
        this.envMap = environmentSceneService.generateCubeMap(this.owner.worldPosition);
        busyBatch.setVisible(busyVisible);
        this.owner.triggerRenderableUpdate(this, RenderableUpdateTriggerState.Changed);
        if (this.envMap) {
            if (DEBUG && !this.debugSphere) {
                this.createDebugSphere();
            }
            else if (this.debugSphere) {
                ;
                this.debugSphere.material.envMap = this.envMap;
            }
        }
    }
    createRenderTarget() {
        if (!this.facet)
            return;
        const resolution = this.facet.params.resolution != undefined ? this.facet.params.resolution : 256;
        const useMipmap = isPowerOfTwo(resolution);
        this.cubeRenderTarget = new WebGLCubeRenderTarget(resolution, {
            // format: RGBFormat,
            generateMipmaps: useMipmap,
            minFilter: LinearMipmapLinearFilter,
            colorSpace: SRGBColorSpace
        });
        this.cubeCamera = new CubeCamera(this.facet.params.near != undefined ? this.facet.params.near : 1, this.facet.params.far != undefined ? this.facet.params.far : 1000, this.cubeRenderTarget);
        this.cubeCamera.layers.set(CameraLayers.Reflection);
    }
    createDebugSphere() {
        const material = new MeshStandardMaterial({
            envMap: this.envMap,
            roughness: 0.0,
            metalness: 1
        });
        this.debugSphere = new Mesh(new IcosahedronGeometry(0.1, 8), material);
        this.debugSphere.matrixAutoUpdate = false;
        mainScene.add(this.debugSphere);
    }
}
